import type { ReactNode } from "react";
import type { SectionSummary } from "../../lib/sectionSummary";

// Shared Overview section shell (Round 13, Phase 1). Every section below
// "Needs attention" collapses to a single counted summary row and expands on
// tap; expansion is persisted per case by the parent. Nothing renders at full
// depth by default. The summary row separates actionable (prominent) from
// ambient (secondary) counts via SectionSummaryView.

export function SectionSummaryView({ summary }: { summary: SectionSummary }) {
  const nodes: Array<{ text: string; cls: string }> = [];
  if (summary.actionable === 0 && summary.calm)
    nodes.push({ text: summary.calm, cls: "ov-summary-calm" });
  for (const p of summary.parts)
    nodes.push({
      text: p.text,
      cls: p.tone === "act" ? "ov-summary-act" : "ov-summary-amb",
    });
  if (nodes.length === 0) return null;
  return (
    <span className="overview-section-summary">
      {nodes.map((n, i) => (
        <span key={i} className={n.cls}>
          {i > 0 ? " · " : ""}
          {n.text}
        </span>
      ))}
    </span>
  );
}

export default function OverviewSection({
  id,
  title,
  summary,
  open,
  onToggle,
  aiMark,
  children,
}: {
  id: string;
  title: ReactNode;
  summary?: SectionSummary | null;
  open: boolean;
  onToggle: () => void;
  // Optional AI-provenance mark rendered next to the title (e.g. category
  // sections whose grouping is AI-classified).
  aiMark?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      className={`overview-section overview-anchor${open ? " overview-section-open" : ""}`}
      id={id}
    >
      {/* The header stays in the flow and is sticky while open (CSS) so it
          never scrolls away — tapping it (rotating chevron) collapses the
          section the same way it was opened (Round 13.1). */}
      <button
        type="button"
        className="overview-section-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="fact-rec-caret">{open ? "▾" : "▸"}</span>
        <h3 className="overview-section-title">
          {title}
          {aiMark}
        </h3>
        {summary && <SectionSummaryView summary={summary} />}
      </button>
      {open && (
        <>
          {children}
          {/* A second, always-reachable way back at the end of a long list. */}
          <button
            type="button"
            className="overview-section-collapse"
            onClick={onToggle}
          >
            ▴ Collapse section
          </button>
        </>
      )}
    </section>
  );
}
